import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { getWesternZodiac, ZODIAC_EXTRAS, zodiacSlug } from "@/lib/calendar/zodiac";
import { MONTH_NAMES_ID, MONTH_SLUGS_ID, parseBareDateSlug, calendarMonthPath, calendarDatePath, daysInMonth } from "@/lib/calendar/dates";
import { withSlash } from "@/lib/url";

export const Route = createFileRoute("/zodiak/tanggal/$slug")({
  loader: ({ params }) => {
    const md = parseBareDateSlug(params.slug);
    if (!md) throw notFound();
    return md;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { d, m } = loaderData;
    const monthName = MONTH_NAMES_ID[m - 1];
    const z = getWesternZodiac(m, d);
    const title = `Lahir ${d} ${monthName} Zodiaknya Apa? ${z.nameId}`;
    const desc = `Yang lahir pada tanggal ${d} ${monthName} memiliki zodiak ${z.nameId} (${z.rangeId}). Cek sifat, elemen, dan zodiak yang cocok.`;
    const canonical = `https://hari.co.id${withSlash(`/zodiak/tanggal/${params.slug}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: ZodiakTanggal,
  notFoundComponent: () => (
    <SiteShell>
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Tanggal tidak valid</h1>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="py-16 text-center">
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Coba lagi</button>
        </div>
      </SiteShell>
    );
  },
});

function ZodiakTanggal() {
  const { d, m } = Route.useLoaderData();
  const monthName = MONTH_NAMES_ID[m - 1];
  const z = getWesternZodiac(m, d);
  const x = ZODIAC_EXTRAS[z.id];

  // Adjacent dates for navigation
  const prevDay = d === 1 ? null : d - 1;
  const nextDay = d === 31 ? null : d + 1;
  const currentYear = new Date().getFullYear();
  const safeDay = Math.min(d, daysInMonth(currentYear, m));

  return (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-10">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link>{" / "}
          <Link to={withSlash("/zodiak") as never} className="hover:text-primary">Zodiak</Link>{" / Tanggal "}
          {d} {monthName}
        </nav>
        <h1 className="font-display text-3xl tracking-tight md:text-4xl">
          Lahir {d} {monthName}, Zodiaknya {z.nameId}
        </h1>
        <p className="mt-3 text-muted-foreground">
          Tanggal {d} {monthName} masuk dalam rentang <strong>{z.rangeId}</strong>.
          Maka zodiakmu adalah <strong>{z.nameId}</strong> {z.symbol} dengan elemen {z.elementId}.
        </p>

        <Link
          to={withSlash(`/zodiak/${zodiacSlug(z)}`) as never}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
        >
          Baca profil lengkap {z.nameId} →
        </Link>

        {x && (
          <p className="mt-6 text-sm leading-relaxed">{x.shortBioId}</p>
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-3 text-sm">
          <div className="rounded-md border border-border bg-card p-3"><div className="text-muted-foreground">Elemen</div><div className="font-semibold">{z.elementId}</div></div>
          <div className="rounded-md border border-border bg-card p-3"><div className="text-muted-foreground">Planet</div><div className="font-semibold">{z.ruler}</div></div>
          {x && <div className="rounded-md border border-border bg-card p-3"><div className="text-muted-foreground">Modalitas</div><div className="font-semibold">{x.modality}</div></div>}
        </div>

        <nav className="mt-10 flex justify-between text-sm">
          {prevDay ? (
            <Link to={withSlash(`/zodiak/tanggal/${prevDay}-${MONTH_SLUGS_ID[m - 1]}`) as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
              ← {prevDay} {monthName}
            </Link>
          ) : <span />}
          {nextDay ? (
            <Link to={withSlash(`/zodiak/tanggal/${nextDay}-${MONTH_SLUGS_ID[m - 1]}`) as never} className="rounded-md border border-border px-3 py-1.5 hover:bg-accent">
              {nextDay} {monthName} →
            </Link>
          ) : <span />}
        </nav>

        <section className="mt-10 rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-semibold">Lihat di Kalender Indonesia</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Cek tanggal {d} {monthName} {currentYear} di kalender Masehi: hari, weton Jawa, hijriah, dan apakah libur nasional.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <Link
              to={withSlash(calendarDatePath(currentYear, m, safeDay)) as never}
              className="rounded-md bg-primary px-3 py-1.5 text-primary-foreground hover:opacity-90"
            >
              {safeDay} {monthName} {currentYear} →
            </Link>
            <Link
              to={withSlash(calendarMonthPath(currentYear, m)) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Kalender {monthName} {currentYear}
            </Link>
            <Link
              to={withSlash(`/kalender/${currentYear}`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Kalender {currentYear}
            </Link>
            <Link
              to={withSlash(`/zodiak/${zodiacSlug(z)}`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Profil {z.nameId}
            </Link>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}